/**
 * PotreeV2Loader - 将 OctreeLoader 包装为 AssetLoader 接口的适配器
 *
 * 职责：
 * 1. 实现 AssetLoader<PointCloudLoadRequest, PointCloudLoadResult> 接口
 * 2. 自行 fetch metadata（携带 AbortSignal 实现 hard-cancel）
 * 3. 复用 OctreeLoader.parseAttributes 解析属性
 * 4. 将 OctreeGeometry/OctreeGeometryNode 适配为 PointCloudLoadResult
 */

import * as THREE from 'three'
import { OctreeGeometry, OctreeGeometryNode } from '@/plugins/pointCloud/core/OctreeGeometry'
import type { OctreeLoadResult } from '@/plugins/pointCloud/types'
import type { AssetLoader, LoadContext } from '@/shared/types/assets'
import type {
  NodeLoader as INodeLoader,
  PointAttributes as IPointAttributes,
  PointBuffer,
  PointCloudNode,
} from '@/shared/types/core'
import type { PointCloudLoadRequest } from '@/shared/types/services'
import { NodeLoader, type NodeLoaderDeps, OctreeLoader } from './OctreeLoader'

type AdaptedPointCloudNode = PointCloudNode & { buffer?: PointBuffer }

// ==================== 辅助函数 ====================

/**
 * 合并两个 AbortSignal，任一触发即中止
 * 返回合并后的 signal 和清理函数
 */
function mergeSignals(
  s1?: AbortSignal,
  s2?: AbortSignal
): { signal: AbortSignal; cleanup: () => void } {
  // 两个都没有，创建一个永不中止的
  if (!s1 && !s2) {
    const ac = new AbortController()
    return { signal: ac.signal, cleanup: () => {} }
  }
  // 只有一个，直接返回
  if (!s1) return { signal: s2!, cleanup: () => {} }
  if (!s2) return { signal: s1, cleanup: () => {} }

  // 任一已中止，直接返回已中止的
  if (s1.aborted) return { signal: s1, cleanup: () => {} }
  if (s2.aborted) return { signal: s2, cleanup: () => {} }

  const ac = new AbortController()

  const onAbort = () => ac.abort()
  s1.addEventListener('abort', onAbort)
  s2.addEventListener('abort', onAbort)

  const cleanup = () => {
    s1.removeEventListener('abort', onAbort)
    s2.removeEventListener('abort', onAbort)
  }

  return { signal: ac.signal, cleanup }
}

function createAbortError(): DOMException {
  return new DOMException('Aborted', 'AbortError')
}

function createFetchInit(
  source: Pick<PointCloudLoadRequest, 'withCredentials'>,
  init: RequestInit = {}
): RequestInit {
  return {
    ...init,
    ...(source.withCredentials === true ? { credentials: 'include' as RequestCredentials } : {}),
  }
}

function getBytesPerElement(array: ArrayBufferView & ArrayLike<number>): number {
  return array.length === 0 ? 0 : array.byteLength / array.length
}

async function waitForGeometryNodeLoad(
  node: OctreeGeometryNode,
  signal?: AbortSignal
): Promise<void> {
  if (signal?.aborted) {
    throw createAbortError()
  }

  if (node.loaded || node.structural) {
    return
  }

  await new Promise<void>((resolve, reject) => {
    let timer: ReturnType<typeof setTimeout> | undefined

    const cleanup = () => {
      if (timer !== undefined) {
        clearTimeout(timer)
      }
      signal?.removeEventListener('abort', onAbort)
    }

    const onAbort = () => {
      cleanup()
      reject(createAbortError())
    }

    const poll = () => {
      if (signal?.aborted) {
        cleanup()
        reject(createAbortError())
        return
      }

      if (node.loaded || node.structural) {
        cleanup()
        resolve()
        return
      }

      if (!node.loading) {
        cleanup()
        resolve()
        return
      }

      timer = setTimeout(poll, 0)
    }

    signal?.addEventListener('abort', onAbort)
    poll()
  })
}

async function waitForGeometryNodeRetry(
  node: OctreeGeometryNode,
  signal?: AbortSignal
): Promise<void> {
  if (signal?.aborted) {
    throw createAbortError()
  }

  const retryAt = Number(node.retryAfter)
  if (!Number.isFinite(retryAt)) {
    throw new Error(`Failed to load Potree node "${node.name}"`)
  }

  const delay = Math.max(0, retryAt - (globalThis.performance?.now?.() ?? Date.now()))
  if (delay === 0) {
    return
  }

  await new Promise<void>((resolve, reject) => {
    let timer: ReturnType<typeof setTimeout> | undefined
    const onAbort = () => {
      if (timer !== undefined) {
        clearTimeout(timer)
      }
      signal?.removeEventListener('abort', onAbort)
      reject(createAbortError())
    }

    signal?.addEventListener('abort', onAbort, { once: true })
    if (signal?.aborted) {
      onAbort()
      return
    }
    timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort)
      resolve()
    }, delay)
  })
}

async function waitForGeometryNodeLoadSlot(
  loader: NodeLoader,
  node: OctreeGeometryNode,
  signal?: AbortSignal
): Promise<void> {
  if (signal?.aborted || loader.isDisposed) {
    throw createAbortError()
  }

  await new Promise<void>((resolve, reject) => {
    let timer: ReturnType<typeof setTimeout> | undefined

    const cleanup = () => {
      if (timer !== undefined) {
        clearTimeout(timer)
      }
      signal?.removeEventListener('abort', onAbort)
    }

    const onAbort = () => {
      cleanup()
      reject(createAbortError())
    }

    const poll = () => {
      if (signal?.aborted || loader.isDisposed) {
        onAbort()
        return
      }

      if (node.Potree.numNodesLoading < node.Potree.maxNodesLoading) {
        cleanup()
        resolve()
        return
      }

      timer = setTimeout(poll, 0)
    }

    signal?.addEventListener('abort', onAbort)
    poll()
  })
}

async function loadGeometryNode(
  loader: NodeLoader,
  node: OctreeGeometryNode,
  signal?: AbortSignal
): Promise<void> {
  while (!node.loaded && !node.structural) {
    if (signal?.aborted || loader.isDisposed) {
      throw createAbortError()
    }

    if (node.loadPermanentlyFailed || node.disposed || node.octreeGeometry.disposed) {
      throw new Error(`Failed to load Potree node "${node.name}"`)
    }

    if (node.loadFailed) {
      await waitForGeometryNodeRetry(node, signal)
    }

    await loader.load(node, signal)
    if (signal?.aborted || loader.isDisposed) {
      throw createAbortError()
    }
    if (node.loaded || node.structural) {
      return
    }
    if (node.loading) {
      await waitForGeometryNodeLoad(node, signal)
      continue
    }

    await waitForGeometryNodeLoadSlot(loader, node, signal)
  }
}

function toPointBuffer(node: OctreeGeometryNode): PointBuffer {
  const position = node.geometry?.getAttribute?.('position') as THREE.BufferAttribute | undefined
  const array = position?.array as (ArrayBufferView & ArrayLike<number>) | undefined

  if (!position || !array || !ArrayBuffer.isView(array)) {
    throw new Error(`Potree node "${node.name}" 缺少 position 缓冲`)
  }

  const buffer = new Uint8Array(array.buffer, array.byteOffset, array.byteLength).slice().buffer

  return {
    buffer,
    pointCount: position.count,
    stride: position.itemSize * getBytesPerElement(array),
  }
}

/**
 * 将 OctreeGeometryNode 适配为 PointCloudNode 接口，同时构建反向映射
 */
function adaptNode(
  node: OctreeGeometryNode,
  nodeMap: Map<PointCloudNode, OctreeGeometryNode>,
  adaptedCache: Map<OctreeGeometryNode, PointCloudNode>
): PointCloudNode {
  const cached = adaptedCache.get(node)
  if (cached) return cached

  let pointBuffer: PointBuffer | undefined

  const adapted: AdaptedPointCloudNode = {
    get index() {
      return node.index
    },
    get boundingBox() {
      return node.boundingBox
    },
    get pointCount() {
      return node.numPoints
    },
    get level() {
      return node.level ?? 0
    },
    get children() {
      const result: (PointCloudNode | null)[] = []
      for (let i = 0; i < 8; i++) {
        const child = node.children[i]
        result.push(child ? adaptNode(child, nodeMap, adaptedCache) : null)
      }
      return result
    },
    get loaded() {
      return node.loaded ?? false
    },
    /** 兼容外部 potree-core 写入；设为 false 会执行真实卸载并释放当前载荷。 */
    set loaded(v: boolean) {
      if (!v) {
        node.unload()
      } else if (node.geometry) {
        node.markResident()
      }
    },
    get buffer() {
      return pointBuffer
    },
    set buffer(v: PointBuffer | undefined) {
      pointBuffer = v
    },
  }
  adaptedCache.set(node, adapted)
  nodeMap.set(adapted, node)
  return adapted
}

/**
 * 将旧版 PointAttributes 适配为 core 接口的 PointAttributes
 */
function adaptAttributes(attrs: ReturnType<typeof OctreeLoader.parseAttributes>): IPointAttributes {
  return {
    attributes: attrs.attributes.map((a) => ({
      name: a.name,
      type: { ordinal: a.type.ordinal, size: a.type.size },
      numElements: a.numElements,
      byteSize: a.byteSize,
      description: a.description,
      range: a.range,
      initialRange: (a as any).initialRange,
    })),
  }
}

// ==================== NodeLoaderAdapter ====================

/**
 * 将 OctreeLoader 的 NodeLoader 适配为 core NodeLoader 接口
 */
class NodeLoaderAdapter implements INodeLoader {
  /** 底层 OctreeLoader NodeLoader 实例 */
  private readonly _inner: NodeLoader
  /** PointCloudNode → OctreeGeometryNode 反向映射 */
  private readonly _nodeMap: Map<PointCloudNode, OctreeGeometryNode>

  constructor(inner: NodeLoader, nodeMap: Map<PointCloudNode, OctreeGeometryNode>) {
    this._inner = inner
    this._nodeMap = nodeMap
  }

  async load(node: PointCloudNode, signal?: AbortSignal): Promise<PointBuffer> {
    const geoNode = this._nodeMap.get(node)

    if (!geoNode) {
      throw new Error('PointCloudNode 未绑定到底层 OctreeGeometryNode')
    }

    if (node.buffer) {
      return node.buffer
    }

    await loadGeometryNode(this._inner, geoNode, signal)

    const pointBuffer = toPointBuffer(geoNode)
    node.buffer = pointBuffer
    return pointBuffer
  }

  dispose(): void {
    this._inner.dispose()
  }
}

// ==================== PotreeV2Loader ====================

/** Potree V2 格式加载器，将 OctreeLoader 适配为 AssetLoader 接口 */
export class PotreeV2Loader implements AssetLoader<PointCloudLoadRequest, OctreeLoadResult> {
  /** 格式标识，固定为 'potree-v2' */
  readonly format = 'potree-v2'
  /** 识别优先级，值越大越先参与探测 */
  readonly detectPriority = 10

  /** NodeLoader 依赖 */
  private deps: NodeLoaderDeps

  constructor(deps: NodeLoaderDeps) {
    this.deps = deps
  }

  /**
   * 检测 URL 是否指向 metadata.json 资源（允许 query/hash）。
   *
   * @param source - 加载请求
   * @returns URL 匹配 metadata.json 返回 true
   */
  canLoad(source: PointCloudLoadRequest): boolean {
    if (!source.url) {
      return false
    }

    const candidates = [source.url]

    try {
      candidates.push(decodeURIComponent(source.url))
    } catch {
      // ignore malformed encoded urls and keep testing the raw url
    }

    return candidates.some((url) => /(?:^|[\\/])metadata\.json(?:$|[?#])/.test(url))
  }

  /**
   * 加载点云数据，支持 hard-cancel。
   *
   * @param source - 加载请求，含 url 与可选 signal
   * @param context - 加载上下文，提供 signal/onProgress
   * @returns 八叉树加载结果，解析失败或中止抛出异常
   */
  async load(source: PointCloudLoadRequest, context: LoadContext): Promise<OctreeLoadResult> {
    const { signal, cleanup } = mergeSignals(source.signal, context.signal)

    try {
      // 检查是否已中止
      if (signal.aborted) {
        throw createAbortError()
      }

      // 1. 手动 fetch metadata（携带 signal 实现 hard-cancel）
      const response = await fetch(source.url, createFetchInit(source, { signal }))
      if (!response.ok) {
        throw new Error(`Potree metadata 请求返回异常状态: ${response.status}`)
      }
      const metadata = await response.json()

      // 中止检查点
      if (signal.aborted) {
        throw createAbortError()
      }

      // 2. 解析属性
      const attributes = OctreeLoader.parseAttributes(metadata.attributes)

      // 3. 构建 NodeLoader
      const nodeLoader = new NodeLoader(this.deps, source.url, {
        withCredentials: source.withCredentials,
      })
      nodeLoader.metadata = metadata
      nodeLoader.attributes = attributes
      nodeLoader.scale = metadata.scale
      nodeLoader.offset = metadata.offset

      // 4. 构建 OctreeGeometry
      const octree = new OctreeGeometry()
      octree.url = source.url
      octree.spacing = metadata.spacing
      octree.scale = metadata.scale

      // 定位用
      const posAttr = metadata.attributes.find((x: { name: string }) => x.name === 'position')
      if (!posAttr) {
        throw new Error('Potree metadata 缺少 position 属性')
      }
      const posMin = new THREE.Vector3(...posAttr.min)
      const posMax = new THREE.Vector3(...posAttr.max)

      // 实际包围盒
      const bbMin = new THREE.Vector3(...metadata.boundingBox.min)
      const bbMax = new THREE.Vector3(...metadata.boundingBox.max)

      const boundingBox = new THREE.Box3(bbMin, bbMax)
      const miniBoundingBox = new THREE.Box3(posMin, posMax)

      const offset = bbMin.clone()
      const miniOffset = posMin.clone()
      boundingBox.min.sub(offset)
      boundingBox.max.sub(offset)
      miniBoundingBox.min.sub(miniOffset)
      miniBoundingBox.max.sub(miniOffset)

      octree.projection = metadata.projection
      octree.boundingBox = boundingBox
      octree.tightBoundingBox = boundingBox.clone()
      octree.mini_tightBoundingBox = miniBoundingBox.clone()
      octree.boundingSphere = boundingBox.getBoundingSphere(new THREE.Sphere())
      octree.tightBoundingSphere = boundingBox.getBoundingSphere(new THREE.Sphere())
      octree.offset = offset
      octree.pointAttributes = OctreeLoader.parseAttributes(metadata.attributes)
      octree.loader = nodeLoader

      // 中止检查点
      if (signal.aborted) {
        throw createAbortError()
      }

      // 5. 构建根节点
      const root = new OctreeGeometryNode('r', octree, boundingBox, this.deps.potreeConfig)
      root.level = 0
      root.nodeType = 2
      root.hierarchyByteOffset = 0n
      root.hierarchyByteSize = BigInt(metadata.hierarchy.firstChunkSize)
      root.hasChildren = false
      root.spacing = octree.spacing
      root.byteOffset = 0n

      octree.root = root

      // 加载根节点层级数据（携带 signal）
      await loadGeometryNode(nodeLoader, root, signal)

      // 中止检查点
      if (signal.aborted) {
        throw createAbortError()
      }

      // 6. 计算总点数
      // potree 的层级是按需加载的：只统计"当前已发现节点"会低估总数（实测大模型上出现
      // 激活点数 > 总点数）。metadata.json 的 `points` 字段是权威总数，优先采用；
      // 缺失或非法时才回退到已发现节点累加。
      const declaredTotalPoints =
        typeof metadata.points === 'number' &&
        Number.isFinite(metadata.points) &&
        metadata.points > 0
          ? Math.trunc(metadata.points)
          : 0
      let totalPoints = declaredTotalPoints
      if (totalPoints === 0) {
        const countPoints = (node: OctreeGeometryNode) => {
          totalPoints += node.numPoints
          for (let i = 0; i < 8; i++) {
            if (node.children[i]) {
              countPoints(node.children[i]!)
            }
          }
        }
        countPoints(root)
      }
      octree.totalPoints = totalPoints

      // 7. 构建结果（含反向节点映射）
      const nodeMap = new Map<PointCloudNode, OctreeGeometryNode>()
      /** 适配节点缓存，避免重复创建适配对象 */
      const adaptedCache = new Map<OctreeGeometryNode, PointCloudNode>()
      const result: OctreeLoadResult = {
        type: 'octree',
        octreeGeometry: octree,
        metadata: {
          boundingBox: octree.boundingBox!,
          pointCount: totalPoints,
          attributes: adaptAttributes(attributes),
          projection: octree.projection,
          spacing: octree.spacing,
        },
        root: adaptNode(root, nodeMap, adaptedCache),
        nodeLoader: new NodeLoaderAdapter(nodeLoader, nodeMap),
      }

      return result
    } finally {
      cleanup()
    }
  }
}
